import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Search, RefreshCw, ShieldAlert, ExternalLink, Info, Sparkles,
  AlertTriangle, FileText, Download, Loader2,
} from "lucide-react";

const RATING_TAG = {
  Satisfactory: "hs-tag-success",
  Conditional: "hs-tag-warn",
  Unsatisfactory: "hs-tag-danger",
};

const RISK_COLOR = {
  LOW: "bg-emerald-100 text-emerald-800 border-emerald-300",
  MODERATE: "bg-amber-100 text-amber-800 border-amber-300",
  HIGH: "bg-orange-100 text-orange-800 border-orange-300",
  CRITICAL: "bg-red-100 text-red-800 border-red-300",
};

const BASIC_LABELS = {
  unsafe_driving: "Unsafe Driving",
  hours_of_service: "Hours of Service",
  driver_fitness: "Driver Fitness",
  controlled_substances: "Controlled Subst. / Alcohol",
  vehicle_maintenance: "Vehicle Maintenance",
  hazmat: "HazMat Compliance",
  crash_indicator: "Crash Indicator",
};

// FMCSA alert thresholds (general carriers)
const BASIC_THRESHOLD = {
  unsafe_driving: 65,
  hours_of_service: 65,
  crash_indicator: 65,
  driver_fitness: 80,
  controlled_substances: 80,
  vehicle_maintenance: 80,
  hazmat: 80,
};

export default function CarrierLookupCard() {
  const [mode, setMode] = useState("dot");
  const [dot, setDot] = useState("");
  const [mc, setMc] = useState("");
  const [data, setData] = useState(null);          // /api/fmcsa/sms response
  const [review, setReview] = useState(null);      // /api/fmcsa/ai-review response
  const [notes, setNotes] = useState("");
  const [loadingLookup, setLoadingLookup] = useState(false);
  const [loadingReview, setLoadingReview] = useState(false);

  const reset = () => { setData(null); setReview(null); };

  const runLookup = async (refresh = false) => {
    const value = mode === "dot" ? dot : mc;
    if (!value) return;
    setLoadingLookup(true);
    setReview(null);
    try {
      const param = mode === "dot" ? `dot=${value.trim()}` : `mc=${value.trim()}`;
      const refreshStr = refresh ? "&refresh=true" : "";
      const { data: res } = await api.get(`/fmcsa/sms?${param}${refreshStr}`);
      setData(res);
      if (!res?.data_coverage?.carrier_snapshot && !res?.data_coverage?.csa_basic_percentile_scores) {
        toast.warning("No data found for that carrier.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Carrier lookup failed");
    } finally {
      setLoadingLookup(false);
    }
  };

  const runAiReview = async () => {
    if (!data) return;
    setLoadingReview(true);
    try {
      const { data: r } = await api.post("/fmcsa/ai-review", {
        dot: data.dot_number || (mode === "dot" ? dot.trim() : undefined),
        mc: mode === "mc" ? mc.trim() : undefined,
        include_solutions: true,
        custom_notes: notes || null,
      });
      setReview(r);
      toast.success("AI compliance review generated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI review failed.");
    } finally {
      setLoadingReview(false);
    }
  };

  const downloadPdf = (withSolutions) => {
    if (!review?.id) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/fmcsa/ai-review/${review.id}.pdf?include_solutions=${withSolutions ? "true" : "false"}`;
    window.open(url, "_blank", "noopener");
  };

  const snapshot = data?.snapshot || {};
  const sms = data?.sms || {};
  const basics = sms?.basics || {};
  const r = review?.review || null;

  return (
    <div className="hs-card p-6 space-y-6" data-testid="carrier-lookup-card">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <ShieldAlert size={16} className="text-blue-700" />
            <div className="hs-overline">CARRIER LOOKUP · FMCSA + CSA SMS</div>
          </div>
          <h3 className="font-display font-bold text-xl mt-1">Deep Compliance Review</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Enter a DOT or MC number to pull the live Carrier Snapshot, the 7 CSA BASIC
            percentile scores from FMCSA SMS, then generate a Crystal Logistics compliance
            review you can hand to the customer.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button
            onClick={() => { setMode("dot"); reset(); }}
            className={`px-3 py-1.5 border ${mode === "dot" ? "border-blue-700 bg-blue-50 text-blue-700 font-bold" : "border-slate-200 text-slate-500"}`}
            data-testid="carrier-mode-dot"
          >DOT</button>
          <button
            onClick={() => { setMode("mc"); reset(); }}
            className={`px-3 py-1.5 border ${mode === "mc" ? "border-blue-700 bg-blue-50 text-blue-700 font-bold" : "border-slate-200 text-slate-500"}`}
            data-testid="carrier-mode-mc"
          >MC</button>
        </div>
      </div>

      {/* Input row */}
      <div className="flex gap-2 flex-wrap">
        {mode === "dot" ? (
          <input
            className="hs-input font-mono flex-1 min-w-[180px]"
            placeholder="DOT number (e.g. 3489201)"
            value={dot}
            onChange={(e) => setDot(e.target.value.replace(/\D/g, ""))}
            data-testid="carrier-dot-input"
          />
        ) : (
          <input
            className="hs-input font-mono flex-1 min-w-[180px]"
            placeholder="MC number (e.g. 872311)"
            value={mc}
            onChange={(e) => setMc(e.target.value.replace(/[^0-9]/g, ""))}
            data-testid="carrier-mc-input"
          />
        )}
        <button
          onClick={() => runLookup(false)}
          disabled={loadingLookup || (mode === "dot" ? !dot : !mc)}
          className="hs-btn-primary inline-flex items-center gap-2"
          data-testid="carrier-lookup-btn"
        >
          {loadingLookup ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          {loadingLookup ? "Looking up…" : "LOOKUP NEW MC"}
        </button>
        {data && (
          <button
            onClick={() => runLookup(true)}
            disabled={loadingLookup}
            className="hs-btn-secondary p-2"
            title="Force refresh"
            data-testid="carrier-refresh-btn"
          >
            <RefreshCw size={14} />
          </button>
        )}
      </div>

      {/* Result panel */}
      {data && (
        <div className="space-y-5" data-testid="carrier-result">
          {/* Carrier identity strip */}
          <div className="border border-slate-200 bg-slate-50 p-4 grid sm:grid-cols-2 gap-3">
            <div>
              <div className="hs-overline text-[10px]">Legal Name</div>
              <div className="font-bold text-slate-900">{snapshot.legal_name || sms.legal_name || "—"}</div>
              <div className="text-xs text-slate-500 mt-1">
                USDOT <span className="font-mono">{data.dot_number || "—"}</span>
                {snapshot.mc_number && <> · MC <span className="font-mono">{snapshot.mc_number}</span></>}
              </div>
              {snapshot.physical_address && (
                <div className="text-xs text-slate-500 mt-1">{snapshot.physical_address}</div>
              )}
            </div>
            <div className="flex flex-col items-start sm:items-end gap-1">
              {snapshot.safety_rating ? (
                <span className={`hs-tag ${RATING_TAG[snapshot.safety_rating] || "hs-tag-info"}`} data-testid="carrier-safety-rating">
                  {snapshot.safety_rating.toUpperCase()}
                </span>
              ) : <span className="text-xs text-slate-400 font-mono">— NOT RATED</span>}
              {snapshot.out_of_service && (
                <span className="hs-tag hs-tag-danger flex items-center gap-1">
                  <AlertTriangle size={12} /> OUT OF SERVICE
                </span>
              )}
              <div className="text-xs text-slate-500">
                Status: <span className="font-mono">{snapshot.operating_status || "—"}</span> · Allowed: <span className="font-mono">{snapshot.allowed_to_operate || "—"}</span>
              </div>
            </div>
          </div>

          {/* CSA BASIC percentiles — the headline feature */}
          <div>
            <div className="hs-overline text-blue-700 mb-2">CSA BASIC Percentile Scores · live from FMCSA SMS</div>
            {Object.keys(basics).length === 0 ? (
              <div className="p-3 border border-amber-300 bg-amber-50 text-xs text-slate-700">
                CSA BASIC scores could not be pulled from FMCSA SMS for this carrier (carrier may have
                insufficient inspection volume, or the SMS page format changed).
                {" "}<a className="text-blue-700 hover:underline inline-flex items-center gap-1"
                       href={`https://ai.fmcsa.dot.gov/SMS/Carrier/${data.dot_number}/Overview.aspx`} target="_blank" rel="noreferrer">
                  Open FMCSA SMS for this DOT <ExternalLink size={11} />
                </a>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {Object.entries(BASIC_LABELS).map(([key, label]) => {
                  const b = basics[key] || {};
                  const pct = b.percentile;
                  const threshold = BASIC_THRESHOLD[key];
                  const overAlert = pct != null && pct >= threshold;
                  return (
                    <div key={key} className={`border p-3 ${overAlert ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-slate-900">{label}</span>
                        {overAlert && <span className="hs-tag hs-tag-danger text-[10px]">ALERT</span>}
                      </div>
                      <div className="flex items-end gap-2 mt-2">
                        <span className={`text-2xl font-display font-black ${overAlert ? "text-red-600" : pct != null ? "text-slate-900" : "text-slate-400"}`}>
                          {pct != null ? `${pct.toFixed(1)}%` : "—"}
                        </span>
                        <span className="text-xs text-slate-500 mb-1">threshold {threshold}%</span>
                      </div>
                      {b.measure != null && (
                        <div className="text-[11px] text-slate-500 mt-1 font-mono">measure {b.measure}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Inspections + crashes */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="border border-slate-200 p-3">
              <div className="hs-overline text-[10px] mb-2">Inspections · 24mo</div>
              <Row k="Driver" v={`${snapshot.driver_inspections ?? 0} (${snapshot.driver_oos_inspections ?? 0} OOS · ${snapshot.driver_oos_pct ?? "—"}%)`} />
              <Row k="Vehicle" v={`${snapshot.vehicle_inspections ?? 0} (${snapshot.vehicle_oos_inspections ?? 0} OOS · ${snapshot.vehicle_oos_pct ?? "—"}%)`} />
              {(snapshot.hazmat_inspections ?? 0) > 0 && (
                <Row k="Hazmat" v={`${snapshot.hazmat_inspections} (${snapshot.hazmat_oos_inspections ?? 0} OOS)`} />
              )}
            </div>
            <div className="border border-slate-200 p-3">
              <div className="hs-overline text-[10px] mb-2">Crashes · 12mo</div>
              <Row k="Total" v={snapshot.crash_total ?? 0} />
              <Row k="Fatal" v={snapshot.fatal_crash ?? 0} danger={(snapshot.fatal_crash || 0) > 0} />
              <Row k="Injury" v={snapshot.injury_crash ?? 0} />
              <Row k="Tow-away" v={snapshot.tow_crash ?? 0} />
            </div>
          </div>

          {/* AI review trigger */}
          <div className="border border-blue-200 bg-blue-50 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-blue-700" />
              <div className="hs-overline text-blue-700">Crystal Logistics · AI Compliance Review</div>
            </div>
            <p className="text-xs text-slate-700">
              Generate a plain-English review of this carrier's situation. The AI auditor writes
              a reality check, lists every problem it can find, and (optionally) recommends
              corrective actions. You can then print either a <b>findings-only</b> sheet or a
              <b> findings + solutions</b> sheet to hand to your customer.
            </p>
            <textarea
              className="hs-input text-sm min-h-[60px]"
              placeholder="Optional: Crystal Logistics notes for the AI auditor (e.g. 'Customer is asking why insurance went up — focus on driver OOS rate and recent crashes')"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="carrier-notes-input"
            />
            <button
              onClick={runAiReview}
              disabled={loadingReview || !data}
              className="hs-btn-primary inline-flex items-center gap-2"
              data-testid="carrier-ai-review-btn"
            >
              {loadingReview ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {loadingReview ? "Analysing…" : (review ? "RE-RUN AI REVIEW" : "GENERATE AI REVIEW")}
            </button>
          </div>

          {/* AI review result */}
          {r && (
            <div className="space-y-4 border-t-2 border-blue-700 pt-5" data-testid="carrier-review-result">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="hs-overline text-blue-700">Compliance Review · {r.carrier_name || "Carrier"}</div>
                  <h4 className="font-display font-black text-2xl mt-1">{r.carrier_name || "—"}</h4>
                </div>
                <div className={`px-4 py-2 border-2 font-bold text-sm tracking-wider ${RISK_COLOR[r.overall_risk_level] || RISK_COLOR.MODERATE}`}>
                  {r.overall_risk_level || "MODERATE"} RISK
                </div>
              </div>

              {r.reality_check && (
                <div>
                  <div className="hs-overline text-red-700 mb-1">REALITY CHECK</div>
                  <p className="text-sm text-slate-800 leading-relaxed">{r.reality_check}</p>
                </div>
              )}

              {r.executive_summary && (
                <div>
                  <div className="hs-overline mb-1">Executive Summary</div>
                  <p className="text-sm text-slate-700 leading-relaxed">{r.executive_summary}</p>
                </div>
              )}

              {Array.isArray(r.problems_found) && r.problems_found.length > 0 && (
                <div>
                  <div className="hs-overline text-red-700 mb-2">Problems Identified ({r.problems_found.length})</div>
                  <div className="space-y-2">
                    {r.problems_found.map((p, i) => (
                      <div key={i} className="border border-slate-200 p-3">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-sm">{p.title}</span>
                          <span className={`hs-tag ${
                            p.priority === "critical" ? "hs-tag-danger" :
                            p.priority === "high" ? "hs-tag-warn" :
                            "hs-tag-info"
                          } text-[10px]`}>
                            {(p.priority || "medium").toUpperCase()}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">{p.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(r.situation_analysis) && r.situation_analysis.length > 0 && (
                <div>
                  <div className="hs-overline mb-2">Situation Analysis</div>
                  <div className="space-y-2">
                    {r.situation_analysis.map((s, i) => (
                      <div key={i} className="border-l-4 border-slate-300 pl-3 py-1">
                        <div className="text-xs font-bold text-slate-900">{s.area}</div>
                        <div className="text-xs text-slate-600 mt-0.5">{s.finding}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Download buttons — TWO PDFs */}
              <div className="border-t border-slate-200 pt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => downloadPdf(false)}
                  className="hs-btn-secondary inline-flex items-center gap-2"
                  data-testid="carrier-pdf-findings-btn"
                >
                  <FileText size={14} /> Download FINDINGS only
                </button>
                <button
                  onClick={() => downloadPdf(true)}
                  className="hs-btn-primary inline-flex items-center gap-2"
                  data-testid="carrier-pdf-solutions-btn"
                >
                  <Download size={14} /> Download FINDINGS + SOLUTIONS
                </button>
                <span className="text-[11px] text-slate-500 self-center ml-2">
                  Both PDFs are branded "Reviewed by Crystal Logistics".
                </span>
              </div>
            </div>
          )}

          <div className="text-[11px] text-slate-500 flex items-center gap-1">
            <Info size={11} />
            Data sources: FMCSA Mobile QC (Carrier Snapshot) + FMCSA SMS (BASIC percentiles, public).
            Lookups cached 24h.
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v, danger }) {
  return (
    <div className="flex items-center justify-between text-sm border-b border-slate-100 last:border-0 py-1">
      <span className="hs-overline text-[10px]">{k}</span>
      <span className={`font-mono text-right text-xs ${danger ? "text-red-600 font-bold" : "text-slate-900"}`}>
        {v ?? "—"}
      </span>
    </div>
  );
}
